import type { Finish, JoistTopAlignment } from "../../../../http/lock_calculator/types";

/** Coefficients resolved by the backend: the effective value the solver uses and the normative one alone. */
export type CalculationCoefficients = {
  kmod: number | null;
  gammaM: number | null;
  gammaMAluminium: number | null;
  kmodDefault: number | null;
  gammaMDefault: number | null;
  gammaMAluminiumDefault: number | null;
};

/** The γM,alu the solver uses: the user override, else the normative constant (the backend sends the override alone). */
export const effectiveGammaMAluminium = (c: { gammaMAluminium?: number | null; gammaMAluminiumDefault?: number | null }): number | null =>
  c.gammaMAluminium ?? c.gammaMAluminiumDefault ?? null;

/** Domain vocabulary used by calculator features. API vocabulary is isolated in adapters. */
export type LockCalculatorFormValues = {
  environment: {
    standardId: string | null;
    serviceClassId: string | null;
    loadDurationClassId: string | null;
    continuousControl: boolean | null;
    /** "Custom" switch, independent of the standard: when on, the three values below replace the normative ones. */
    customCoefficients: boolean;
    kmodOverride: number | null;
    gammaMConnectionOverride: number | null;
    gammaMAluminiumOverride: number | null;
  };
  lateralResistanceConfig: number | null;
  upliftResistanceConfig: number | null;
  connectorPairCount: 1 | 2 | null;
  connectorPairs: [{ lockItemId: string | null; finish: Finish | null }, { lockItemId: string | null; finish: Finish | null }];
  headerMember: {
    materialGradeId: string | null;
    width: number | null;
    height: number | null;
    orientation: string | null;
  };
  joistMember: {
    materialGradeId: string | null;
    width: number | null;
    height: number | null;
    alphaDeg: number | null;
    betaDeg: number | null;
    /** Distance (mm) from the joist top edge to the connector; null = the backend default. */
    connectorTopDistance: number | null;
    /** Backend default for connectorTopDistance; a value equal to it is not persisted. */
    connectorTopDistanceDefault: number | null;
    /** Column header only: joist top flush with the column top or offset below it. Stored on the configuration. */
    topAlignment: JoistTopAlignment;
    /** mm the joist top sits below the column top (offset mode only); null = the lock's c_min. */
    topOffsetFromHeader: number | null;
    /** Backend default for topOffsetFromHeader (the lock's c_min); a value equal to it is not persisted. */
    topOffsetDefault: number | null;
  };
  fasteners: { headerItemId: string | null; joistItemId: string | null; inclinedItemId: string | null };
  lockstopItemId: string | null;
  bom: { connectionCount: number | null; headerLockItemId: string | null };
  loads: { vertical: number | null; uplift: number | null; axial: number | null; lateral: number | null };
  combinedVerification: { includeVertical: boolean; includeUplift: boolean; includeAxial: boolean; includeLateral: boolean };
};
